/**
 * Add an account for somebody who has never signed in (f3.2, contract repaired
 * by f8.3).
 *
 * **Nothing is sent to the person.** ADR-5 removed the invitation e-mail:
 * there is no token, no expiry, no accept endpoint and no resend. Telling them
 * to sign in is an out-of-band conversation, so this dialog does not promise a
 * message that will never arrive. The subtitle says what actually happens
 * instead: the account is created now and waits, and the person's first Entra
 * sign-in with this address claims it and keeps the role chosen here.
 *
 * Three fields, because the server accepts three and refuses a fourth. There
 * is no scope (f1.3 D2 dropped the one scoped role) and no delivery role: a
 * delivery role is an ASSIGNMENT against a hierarchy node, made from that
 * node's own screen. There is no person link either, and nothing left to link
 * to -- ADR-19's separate roster record is superseded, so **the account this
 * dialog creates IS the delivery-role holder**, nameable as a DEM or a CEM the
 * moment it exists and long before anybody signs in to it.
 *
 * **No role is named in this file.** The picker is f2.2's `PLATFORM_ROLE`
 * catalogue at runtime, so a role inserted in phase 10 or 16 is offered here
 * with no change at all (BR-9) -- the same rule the role dialog follows.
 *
 * A refusal keeps the dialog open with the typed values retained: a taken
 * e-mail is a fact about the platform, and discarding a half-typed form over
 * it would make the Admin start again.
 *
 * ## What f8.3 changed, and why it is a repair and not a fork
 *
 * f3.2's specification required this component to take `open`,
 * `onOpenChange`, `defaultValues` and `onInvited(user)`, and to live **outside
 * the Users screen's route folder**, so that *"importing it from f8.3 creates
 * no dependency on a screen f8.3 does not render"*. What shipped was
 * `routes/users/AddUserDialog.tsx` with `{roles, onClose}` -- mountable only by
 * the Users screen. This file is that component at the contract f3.2 wrote for
 * it. A second dialog would have been exactly the parallel creation path the
 * import feature exists to prevent.
 *
 * `roles` is now OPTIONAL. Given, the caller's catalogue slice is used (the
 * Users screen already holds one). Omitted, the dialog reads the catalogue
 * itself, which is what lets it mount with no provider but the query client.
 *
 * `submitter` is the one seam, and it is a PORT rather than a phase-8 flag.
 * Omitted, the account is created by `POST /users` -- f3.2's own path. The
 * import screen passes a submitter that reaches the very same server-side
 * `invite_user` through its resolution request instead, so that the invite and
 * the staged rows it was made for land in ONE transaction and a refusal rolls
 * both back. Both paths return the created account, and both refuse in the
 * house `{message, reason, errors}` envelope, so everything below this line is
 * identical either way.
 */

import { useState } from 'react';
import { Check } from 'lucide-react';

import type { LookupValue } from '@/api/client';
import { generalMessage, problemFor } from '@/api/fieldErrors';
import { useAddUser, useLookupCatalog } from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';
import { platformRolesFrom } from '@/users/platformRoles';

/** The values the dialog sends. The server's three fields, and no fourth. */
export interface InviteValues {
  email: string;
  display_name: string;
  platform_role_id: string;
}

/**
 * The created account, as every caller needs it.
 *
 * Structural on purpose: `POST /users` answers with a `UserAccount` and the
 * resolution route answers with a `ResolvedAccount`, and both carry these
 * three. Naming either concrete type here would make one caller convert.
 */
export interface InvitedAccount {
  id: string;
  display_name: string;
  email: string;
}

/**
 * How the account is created.
 *
 * Shaped like the slice of a TanStack mutation the dialog actually uses, so a
 * caller passes one straight through.
 */
export interface InviteSubmitter {
  submit: (values: InviteValues) => Promise<InvitedAccount>;
  isPending: boolean;
  /** The refusal, as an `ApiError`, so `problemFor` can bind it to a field. */
  error: unknown;
}

export interface InviteDefaults {
  display_name?: string;
  email?: string;
  /** A catalogue row's id. Never a role code -- the caller resolves it. */
  platform_role_id?: string;
}

export function InviteUserDialog({
  open,
  onOpenChange,
  defaultValues,
  onInvited,
  roles,
  submitter,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Prefilled values. What is absent is left empty, never guessed. */
  defaultValues?: InviteDefaults;
  /** The created account, handed back so the caller can bind it. */
  onInvited?: (user: InvitedAccount) => void;
  /** The caller's catalogue slice. Omitted, the dialog reads its own. */
  roles?: readonly LookupValue[];
  /** Omitted, f3.2's `POST /users`. */
  submitter?: InviteSubmitter;
}) {
  // Mounted only while open, so every field, every typed value and every
  // refusal is discarded on close -- which is what makes `defaultValues`
  // arrive fresh each time the import screen opens it for a different name.
  if (!open) return null;
  return (
    <InviteUserForm
      onOpenChange={onOpenChange}
      defaultValues={defaultValues}
      onInvited={onInvited}
      roles={roles}
      submitter={submitter}
    />
  );
}

function InviteUserForm({
  onOpenChange,
  defaultValues,
  onInvited,
  roles,
  submitter,
}: {
  onOpenChange: (open: boolean) => void;
  defaultValues?: InviteDefaults;
  onInvited?: (user: InvitedAccount) => void;
  roles?: readonly LookupValue[];
  submitter?: InviteSubmitter;
}) {
  // Fetched only when the caller did not supply the slice, so the Users screen
  // does not pay for a second read of a catalogue it already holds.
  const catalog = useLookupCatalog({ enabled: roles === undefined });
  const add = useAddUser();

  const [displayName, setDisplayName] = useState(defaultValues?.display_name ?? '');
  const [email, setEmail] = useState(defaultValues?.email ?? '');
  const [roleId, setRoleId] = useState(defaultValues?.platform_role_id ?? '');

  const available = roles ?? platformRolesFrom(catalog.data);
  // Only ACTIVE roles are offered. Unlike the role dialog there is no stored
  // value to preserve here -- the account does not exist yet -- so a
  // deactivated role has no reason to appear.
  const offered = available.filter((role) => role.is_active);

  const busy = submitter ? submitter.isPending : add.isPending;
  const failure = submitter ? submitter.error : add.error;

  const close = () => onOpenChange(false);

  const submit = () => {
    const values: InviteValues = {
      email,
      display_name: displayName,
      platform_role_id: roleId,
    };
    if (submitter) {
      // The refusal is the submitter's to hold; it re-renders this dialog with
      // `error` set and the typed values still in place. Swallowed here so an
      // unhandled rejection is not also logged for a refusal already shown.
      void submitter.submit(values).then(
        (user) => {
          onInvited?.(user);
          close();
        },
        () => undefined,
      );
      return;
    }
    add.mutate(values, {
      onSuccess: (user) => {
        onInvited?.(user);
        close();
      },
    });
  };

  return (
    <Modal
      title="Add user"
      subtitle="Creates the account now; they claim it by signing in"
      onClose={close}
      onSubmit={submit}
      busy={busy}
      submitLabel="Add user"
      submitIcon={<Check aria-hidden="true" />}
      // Only when the server refused without naming a control, so a field
      // problem is never also shown at the top of the dialog.
      message={generalMessage(failure, 'email', 'display_name', 'platform_role_id')}
    >
      <DialogField
        label="Display name"
        htmlFor="add-user-name"
        problem={problemFor(failure, 'display_name')}
        hint="Replaced by the name on their token at first sign-in"
      >
        <input
          id="add-user-name"
          type="text"
          value={displayName}
          required
          maxLength={256}
          placeholder="Ada Lovelace"
          disabled={busy}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </DialogField>

      <DialogField
        label="Work e-mail"
        htmlFor="add-user-email"
        problem={problemFor(failure, 'email')}
        hint="The address they sign in with"
      >
        <input
          id="add-user-email"
          type="email"
          value={email}
          required
          maxLength={320}
          placeholder="ada.lovelace@gftus.com"
          disabled={busy}
          onChange={(event) => setEmail(event.target.value)}
        />
      </DialogField>

      <DialogField
        label="Platform role"
        htmlFor="add-user-role"
        span
        problem={problemFor(failure, 'platform_role_id')}
        hint="Required. It can be changed afterwards from the roster"
      >
        <select
          id="add-user-role"
          value={roleId}
          required
          disabled={busy}
          onChange={(event) => setRoleId(event.target.value)}
        >
          {/* Empty rather than pre-selected: the first row of the catalogue
              could be any role, and a role nobody chose is not a grant. A
              caller that DOES know which row to start on says so through
              `defaultValues`, and the import says the one granting nothing. */}
          <option value="">Choose a role</option>
          {offered.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
      </DialogField>
    </Modal>
  );
}
